import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function AboutPage() {
  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于桃花视频',
    description: '桃花视频是一个专业的影视资源聚合平台，汇集香蕉视频、糖豆视频、小蓝视频、蓝莓视频等热门影视资源。',
  };

  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '桃花视频',
    url: 'https://taohuavideo.com',
    description: '桃花视频 - 专业的影视资源聚合平台，提供高清免费在线观看服务。',
    foundingDate: '2020',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'support@taohuavideo.com',
      availableLanguage: ['Chinese'],
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '桃花视频需要付费吗？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '桃花视频完全免费，所有影视资源均可免费在线观看，无需任何付费会员。我们通过聚合多个平台的公开资源，为用户提供便捷的观影体验。',
        },
      },
      {
        '@type': 'Question',
        name: '桃花视频的视频资源来自哪里？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '桃花视频聚合了香蕉视频、糖豆视频、小蓝视频、蓝莓视频等多个热门视频平台的公开影视资源，为用户提供一站式的影视浏览和发现服务。',
        },
      },
      {
        '@type': 'Question',
        name: '为什么有些视频无法播放？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '由于视频资源来自各平台，偶尔可能会遇到链接失效的情况。我们会定期检查和更新资源链接，确保绝大多数视频都能正常播放。如遇问题，欢迎通过联系方式反馈。',
        },
      },
      {
        '@type': 'Question',
        name: '桃花视频支持哪些设备？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '桃花视频支持在电脑、平板、手机等多种设备上访问，只需使用浏览器打开网站即可观看，无需下载任何应用。推荐使用Chrome、Safari、Edge等现代浏览器获得最佳体验。',
        },
      },
      {
        '@type': 'Question',
        name: '如何联系桃花视频？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '您可以通过邮箱 support@taohuavideo.com 与我们联系，我们会在24小时内回复您的咨询。也可以在社交媒体上关注我们获取最新影视资讯。',
        },
      },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(aboutSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(organizationSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>

      <Navbar />

      <main className="pt-16">
        {/* Breadcrumb */}
        <div className="bg-background-50 border-b border-background-200/70">
          <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '关于我们' }]} />
        </div>

        {/* Hero Banner */}
        <section className="relative w-full h-[320px] md:h-[380px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Elegant%20soft%20peach%20blossom%20branches%20against%20warm%20gradient%20background%2C%20delicate%20pink%20petals%20floating%2C%20minimalist%20artistic%20composition%2C%20dreamy%20spring%20atmosphere%2C%20clean%20modern%20aesthetic%2C%20soft%20natural%20light&width=1920&height=500&seq=taohua-about-hero&orientation=landscape"
            alt="关于桃花视频"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/15 to-black/40"></div>
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
            <h1 className="text-3xl md:text-5xl font-heading font-bold text-white mb-3">
              关于桃花视频
            </h1>
            <p className="text-sm md:text-base text-white/80 max-w-xl">
              聚合优质影视，让追剧更简单
            </p>
          </div>
        </section>

        {/* Brand Introduction */}
        <section className="w-full px-4 md:px-6 lg:px-8 py-12 md:py-16 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-6 text-center">
              品牌介绍
            </h2>
            <div className="space-y-5 text-sm text-foreground-700 leading-relaxed">
              <p>
                <strong className="text-foreground-950">桃花视频</strong>成立于2020年，是一个专注于影视资源聚合与发现的在线平台。
                我们深知追剧爱好者的需求——大家都希望在一个地方就能找到想看的全部内容。
                因此，桃花视频应运而生，致力于为用户提供一站式的影视浏览体验。
              </p>
              <p>
                我们汇集了<strong className="text-foreground-950">香蕉视频</strong>、<strong className="text-foreground-950">糖豆视频</strong>、
                <strong className="text-foreground-950">小蓝视频</strong>、<strong className="text-foreground-950">蓝莓视频</strong>等
                多个热门影视平台的优质资源，涵盖电视剧、电影、综艺、动漫、纪录片等多种类型。
                无论你是想看最新的热门剧集，还是回顾经典老片，桃花视频都能满足你。
              </p>
              <p>
                我们坚持<strong className="text-foreground-950">免费、高清、流畅</strong>的服务理念。
                所有资源均可免费在线观看，无需注册，无需付费，打开即看。
                同时，我们不断优化播放体验，确保视频播放流畅不缓冲，让你沉浸在精彩的影视世界中。
              </p>
              <p>
                桃花视频的团队由一群热爱影视的年轻人组成。我们每天都在关注最新的影视动态，
                第一时间更新剧集和电影资源，让你的追剧清单永远保持新鲜。
                我们相信，好的影视作品值得被更多人看到，桃花视频就是连接好内容与观众的桥梁。
              </p>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="w-full px-4 md:px-6 lg:px-8 py-12 md:py-16 bg-background-100">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-10 text-center">
              为什么选择桃花视频
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
              {[
                {
                  icon: 'ri-movie-line',
                  title: '海量资源',
                  desc: '聚合多平台优质影视，电视剧、电影、综艺、动漫、纪录片一应俱全',
                },
                {
                  icon: 'ri-shield-check-line',
                  title: '完全免费',
                  desc: '所有资源免费在线观看，无需注册，无需付费，打开即看',
                },
                {
                  icon: 'ri-speed-up-line',
                  title: '流畅不缓冲',
                  desc: '持续优化播放体验，99.8%的流畅率保障，观影不卡顿',
                },
                {
                  icon: 'ri-refresh-line',
                  title: '实时更新',
                  desc: '最新剧集同步更新，热门影视第一时间上线，追剧不等待',
                },
              ].map((feature) => (
                <div
                  key={feature.title}
                  className="bg-background-50 rounded-xl p-5 md:p-6 text-center border border-background-200/70"
                >
                  <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center bg-primary-100 rounded-xl">
                    <i className={`${feature.icon} text-xl text-primary-600`}></i>
                  </div>
                  <h3 className="font-heading font-semibold text-foreground-950 mb-2 text-base">
                    {feature.title}
                  </h3>
                  <p className="text-xs text-foreground-600 leading-relaxed">
                    {feature.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Reputation */}
        <section className="w-full px-4 md:px-6 lg:px-8 py-12 md:py-16 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-10 text-center">
              用户口碑
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {[
                {
                  name: '追剧达人小王',
                  comment: '桃花视频真的太好用了！以前要在好几个APP之间切换，现在一个网站就能看到所有想看的剧。更新速度也很快，狂飙更新那天我就第一时间看到了！',
                  rating: 5,
                },
                {
                  name: '电影爱好者阿美',
                  comment: '作为一名电影爱好者，桃花视频的电影资源真的太丰富了。从国产大片到小众文艺片应有尽有，而且画质清晰，播放流畅，强烈推荐！',
                  rating: 5,
                },
                {
                  name: '动漫宅小李',
                  comment: '斗罗大陆更新必追！桃花视频的动漫频道做得很好，分类清晰，找番很方便。画质高清，加载速度也快，看番体验满分。',
                  rating: 5,
                },
                {
                  name: '综艺达人张姐',
                  comment: '种地吧每一期都在桃花视频上追的！画质清楚不卡顿，弹幕氛围也好。推荐给所有喜欢看综艺的朋友们。',
                  rating: 4,
                },
              ].map((review) => (
                <div
                  key={review.name}
                  className="bg-background-100 rounded-xl p-5 md:p-6 border border-background-200/70"
                >
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: review.rating }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-accent-500 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                    &ldquo;{review.comment}&rdquo;
                  </p>
                  <p className="text-xs font-medium text-foreground-900">{review.name}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="w-full px-4 md:px-6 lg:px-8 py-12 md:py-16 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3 text-center">
              联系我们
            </h2>
            <p className="text-sm text-foreground-600 text-center mb-10">
              如有任何问题或建议，欢迎通过以下方式与我们联系
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              <div className="bg-background-50 rounded-xl p-5 text-center border border-background-200/70">
                <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center bg-accent-100 rounded-xl">
                  <i className="ri-mail-line text-xl text-accent-600"></i>
                </div>
                <h3 className="font-heading font-semibold text-foreground-950 mb-1 text-sm">电子邮箱</h3>
                <p className="text-xs text-foreground-600">support@taohuavideo.com</p>
              </div>
              <div className="bg-background-50 rounded-xl p-5 text-center border border-background-200/70">
                <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center bg-accent-100 rounded-xl">
                  <i className="ri-weibo-line text-xl text-accent-600"></i>
                </div>
                <h3 className="font-heading font-semibold text-foreground-950 mb-1 text-sm">微博</h3>
                <p className="text-xs text-foreground-600">@桃花视频官方</p>
              </div>
              <div className="bg-background-50 rounded-xl p-5 text-center border border-background-200/70">
                <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center bg-accent-100 rounded-xl">
                  <i className="ri-wechat-line text-xl text-accent-600"></i>
                </div>
                <h3 className="font-heading font-semibold text-foreground-950 mb-1 text-sm">微信公众号</h3>
                <p className="text-xs text-foreground-600">桃花视频</p>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}